import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import FileUpload from './FileUpload';

function uid() { return Math.random().toString(36).slice(2, 10); }

export default function ShowcaseEditor({ value, onChange }) {
  const v = value || { enabled: false, title: 'Serviços & Produtos', items: [] };
  const upd = (patch) => onChange({ ...v, ...patch });
  const items = v.items || [];

  const addItem = (type = 'service') => upd({
    items: [...items, {
      id: uid(), type, name: `Novo ${type === 'service' ? 'serviço' : 'produto'}`,
      description: '', image: '', price: 0, salePrice: 0, category: '',
      order: items.length, enabled: true, durationMin: type === 'service' ? 30 : 0,
      whatsappPhone: '', whatsappMessage: ''
    }]
  });
  const updItem = (i, patch) => { const arr = [...items]; arr[i] = { ...arr[i], ...patch }; upd({ items: arr }); };
  const delItem = (i) => upd({ items: items.filter((_, idx) => idx !== i) });
  const move = (i, dir) => {
    const arr = [...items]; const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    upd({ items: arr.map((x, idx) => ({ ...x, order: idx })) });
  };

  const sorted = [...items.map((it, idx) => ({ ...it, _idx: idx }))].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-neutral-200 p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div><Label>Ativar vitrine no biosite</Label>
            <p className="text-xs text-neutral-500">Exibe a seção "Serviços & Produtos" com cards na página pública.</p></div>
          <Switch checked={!!v.enabled} onCheckedChange={(x) => upd({ enabled: x })} />
        </div>
        <div>
          <Label className="text-xs">Título da seção</Label>
          <Input value={v.title || ''} onChange={(e) => upd({ title: e.target.value })} className="mt-1" />
        </div>
      </div>

      {sorted.map((it) => {
        const i = it._idx;
        return (
          <div key={it.id} className={`bg-white rounded-2xl border border-neutral-200 p-4 ${it.enabled === false ? 'opacity-60' : ''}`}>
            <div className="flex items-start gap-2">
              <div className="flex flex-col gap-1 pt-1 text-neutral-400">
                <button onClick={() => move(i, -1)} className="hover:text-neutral-900 text-xs">▲</button>
                <GripVertical size={14} />
                <button onClick={() => move(i, 1)} className="hover:text-neutral-900 text-xs">▼</button>
              </div>
              <div className="flex-1 grid sm:grid-cols-[140px_1fr] gap-4">
                <FileUpload value={it.image} onChange={(x) => updItem(i, { image: x })} aspect="wide" label="Foto" maxW={900} />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Tipo</Label>
                    <Select value={it.type} onValueChange={(x) => updItem(i, { type: x })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="service">Serviço</SelectItem>
                        <SelectItem value="product">Produto</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Categoria</Label>
                    <Input value={it.category || ''} onChange={(e) => updItem(i, { category: e.target.value })} placeholder="ex: Cabelo" className="mt-1" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Nome</Label>
                    <Input value={it.name} onChange={(e) => updItem(i, { name: e.target.value })} className="mt-1" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Descrição curta</Label>
                    <Textarea rows={2} value={it.description} onChange={(e) => updItem(i, { description: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Preço (R$)</Label>
                    <Input type="number" step="0.01" min="0" value={it.price} onChange={(e) => updItem(i, { price: parseFloat(e.target.value) || 0 })} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Preço promocional</Label>
                    <Input type="number" step="0.01" min="0" value={it.salePrice} onChange={(e) => updItem(i, { salePrice: parseFloat(e.target.value) || 0 })} className="mt-1" />
                  </div>
                  {it.type === 'service' && (
                    <div>
                      <Label className="text-xs">Duração (min)</Label>
                      <Input type="number" min="5" step="5" value={it.durationMin} onChange={(e) => updItem(i, { durationMin: parseInt(e.target.value) || 0 })} className="mt-1" />
                    </div>
                  )}
                  <div className={it.type === 'service' ? '' : 'col-span-2'}>
                    <Label className="text-xs">WhatsApp (código país+DDD+número)</Label>
                    <Input value={it.whatsappPhone} onChange={(e) => updItem(i, { whatsappPhone: e.target.value })} placeholder="5511999999999" className="mt-1" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Mensagem WhatsApp (opcional)</Label>
                    <Input value={it.whatsappMessage} onChange={(e) => updItem(i, { whatsappMessage: e.target.value })} placeholder={`Oi! Quero ${it.name}`} className="mt-1" />
                  </div>
                  <div className="col-span-2 flex items-center gap-2 text-xs text-neutral-600 pt-1">
                    <Switch checked={it.enabled !== false} onCheckedChange={(x) => updItem(i, { enabled: x })} />
                    {it.enabled === false ? 'Item desativado' : 'Item ativo'}
                  </div>
                </div>
              </div>
              <Button variant="outline" size="icon" onClick={() => delItem(i)} className="border-neutral-300 text-red-600 hover:bg-red-50 self-start">
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        );
      })}

      <div className="flex gap-2">
        <Button variant="outline" onClick={() => addItem('service')} className="flex-1 gap-2 border-dashed border-neutral-300 h-12"><Plus size={16} /> Adicionar serviço</Button>
        <Button variant="outline" onClick={() => addItem('product')} className="flex-1 gap-2 border-dashed border-neutral-300 h-12"><Plus size={16} /> Adicionar produto</Button>
      </div>
    </div>
  );
}
